/**
 * 管理者ログイン(docs/20 §3)。
 *
 *   1. **メール+パスワードのみ**。新規登録UIは作らない
 *   2. サインイン後に ID トークンのカスタムクレーム `role`(owner / editor / viewer)を見る
 *      (付与・剥奪は owner が「権限」画面から。最初の owner はユーザーが CLI で付与する)
 *   3. 移行期間だけ、role が無く Firestore `admins/{uid}` があるユーザーを viewer として通す
 *   4. どちらも無ければ即サインアウトして「権限がありません」を出す
 *
 * 画面の出し分けはこの role で行うが、**権限の本当の判定は管理用 Functions が毎回行う**。
 */
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { doc, getDoc, type Firestore } from 'firebase/firestore';

/** 管理者ドキュメントのコレクション名。ルール(firebase/firestore.rules)と揃える。 */
export const ADMINS_COLLECTION = 'admins';

export type AdminAuthErrorCode =
  | 'invalid-credential'
  | 'invalid-email'
  | 'too-many-requests'
  | 'network'
  | 'unsupported'
  | 'not-admin'
  | 'unknown';

export class AdminAuthError extends Error {
  constructor(
    readonly code: AdminAuthErrorCode,
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'AdminAuthError';
  }
}

const MESSAGES: Readonly<Record<AdminAuthErrorCode, string>> = {
  'invalid-credential': 'メールアドレスまたはパスワードが違います。',
  'invalid-email': 'メールアドレスの形式が正しくありません。',
  'too-many-requests': '試行回数が多すぎます。しばらく待ってからやり直してください。',
  network: 'ネットワークに接続できませんでした。',
  unsupported: 'この環境ではログインが利用できません(Auth の設定を確認してください)。',
  'not-admin': '権限がありません。このアカウントは管理者として登録されていません。',
  unknown: 'ログインに失敗しました。',
};

/** 画面にそのまま出せる日本語メッセージ。 */
export function messageFor(code: AdminAuthErrorCode): string {
  return MESSAGES[code];
}

const CODE_MAP: Readonly<Record<string, AdminAuthErrorCode>> = {
  'auth/invalid-credential': 'invalid-credential',
  'auth/wrong-password': 'invalid-credential',
  'auth/user-not-found': 'invalid-credential',
  'auth/user-disabled': 'invalid-credential',
  'auth/invalid-email': 'invalid-email',
  'auth/missing-email': 'invalid-email',
  'auth/missing-password': 'invalid-credential',
  'auth/too-many-requests': 'too-many-requests',
  'auth/network-request-failed': 'network',
  'auth/operation-not-allowed': 'unsupported',
};

function readCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * SDK のエラーを画面用の `AdminAuthError` に翻訳する。
 *
 * ユーザー列挙を避けるため `user-not-found` も「メールかパスワードが違う」に寄せる
 * (Firebase の既定でも email enumeration protection が有効なら同じ扱いになる)。
 */
export function toAdminAuthError(error: unknown): AdminAuthError {
  if (error instanceof AdminAuthError) return error;
  const code = CODE_MAP[readCode(error)] ?? 'unknown';
  return new AdminAuthError(code, MESSAGES[code], error);
}

/** 管理者の権限(docs/20 §3)。 */
export const ADMIN_ROLES = ['owner', 'editor', 'viewer'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ROLE_LABELS: Readonly<Record<AdminRole, string>> = {
  owner: 'owner(全権+権限付与)',
  editor: 'editor(編集・プレビュー・公開)',
  viewer: 'viewer(閲覧)',
};

export function isAdminRole(value: unknown): value is AdminRole {
  return typeof value === 'string' && (ADMIN_ROLES as readonly string[]).includes(value);
}

/** 編集・プレビュー作成・公開・取り消しができるか(editor 以上)。 */
export function canEdit(role: AdminRole): boolean {
  return role === 'owner' || role === 'editor';
}

/** 権限の付与・剥奪ができるか(owner のみ)。 */
export function canManageRoles(role: AdminRole): boolean {
  return role === 'owner';
}

export interface AdminSession {
  readonly uid: string;
  readonly email: string | null;
  readonly role: AdminRole;
  /** role クレームが無く、移行期間の `admins/{uid}` で通した(viewer 扱い)。 */
  readonly legacy: boolean;
}

export type AdminState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'signedOut'; readonly error: AdminAuthError | null }
  | { readonly kind: 'signedIn'; readonly session: AdminSession };

/**
 * ID トークンの role クレーム。付与直後でも反映されるよう、トークンを取り直して読む。
 */
export async function readRoleClaim(
  user: Pick<User, 'getIdTokenResult'>,
): Promise<AdminRole | null> {
  const token = await user.getIdTokenResult(true);
  const role = token.claims.role;
  return isAdminRole(role) ? role : null;
}

/** `admins/{uid}` が存在するか(移行期間の viewer 判定)。 */
export async function isAdminUid(db: Firestore, uid: string): Promise<boolean> {
  const snapshot = await getDoc(doc(db, ADMINS_COLLECTION, uid));
  return snapshot.exists();
}

export interface AdminAuthDeps {
  readonly auth: Auth;
  readonly db: Firestore;
}

/**
 * ログイン状態の管理。
 *
 * `onAuthStateChanged` を単一の真実にしている — ページ再読込で復元された
 * セッションも、ログイン直後のセッションも**同じ経路で** admins 判定を通す。
 * (ログイン成功パスだけで判定すると、リロードで判定を飛ばせてしまう)
 */
export class AdminAuthService {
  private state: AdminState = { kind: 'loading' };
  private readonly listeners = new Set<(state: AdminState) => void>();
  /** サインイン時の判定が終わるまで signIn() を待たせるための解決関数。 */
  private pending: ((state: AdminState) => void) | null = null;

  constructor(private readonly deps: AdminAuthDeps) {
    onAuthStateChanged(this.deps.auth, (user) => {
      void this.handleUser(user);
    });
  }

  current(): AdminState {
    return this.state;
  }

  subscribe(listener: (state: AdminState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /**
   * メール+パスワードでログインし、管理者判定まで終わった状態を返す。
   *
   * 判定は `onAuthStateChanged` 側で走るので、その結果を待ってから返す。
   */
  async signIn(email: string, password: string): Promise<AdminState> {
    const settled = new Promise<AdminState>((resolve) => {
      this.pending = resolve;
    });
    try {
      await signInWithEmailAndPassword(this.deps.auth, email, password);
    } catch (error) {
      this.pending = null;
      const authError = toAdminAuthError(error);
      this.publish({ kind: 'signedOut', error: authError });
      throw authError;
    }
    const state = await settled;
    if (state.kind !== 'signedIn') {
      throw state.kind === 'signedOut' && state.error
        ? state.error
        : new AdminAuthError('unknown', MESSAGES.unknown);
    }
    return state;
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(this.deps.auth);
    this.publish({ kind: 'signedOut', error: null });
  }

  /** 認証状態の変化を1本にまとめて処理する(復元・ログイン・ログアウト共通)。 */
  private async handleUser(user: User | null): Promise<void> {
    if (!user) {
      // signIn() 側で既にエラーを立てている場合はそれを消さない。
      if (this.state.kind !== 'signedOut') this.publish({ kind: 'signedOut', error: null });
      return;
    }
    try {
      const role = await readRoleClaim(user);
      if (role !== null) {
        this.publish({
          kind: 'signedIn',
          session: { uid: user.uid, email: user.email, role, legacy: false },
        });
        return;
      }
      // 移行期間: role が無くても admins/{uid} があれば viewer として通す(docs/20 §3)。
      if (await isAdminUid(this.deps.db, user.uid).catch(() => false)) {
        this.publish({
          kind: 'signedIn',
          session: { uid: user.uid, email: user.email, role: 'viewer', legacy: true },
        });
        return;
      }
      // 管理者でないアカウントを掴んだままにしない。
      await firebaseSignOut(this.deps.auth).catch(() => undefined);
      this.publish({
        kind: 'signedOut',
        error: new AdminAuthError('not-admin', MESSAGES['not-admin']),
      });
    } catch (error) {
      // トークンが読めない = 管理者と確かめられない、として扱う。
      await firebaseSignOut(this.deps.auth).catch(() => undefined);
      this.publish({
        kind: 'signedOut',
        error: new AdminAuthError('not-admin', MESSAGES['not-admin'], error),
      });
    }
  }

  private publish(state: AdminState): void {
    this.state = state;
    const pending = this.pending;
    this.pending = null;
    pending?.(state);
    for (const listener of this.listeners) listener(state);
  }
}
